import { CalendarClock, Check, Minus } from "lucide-react";
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "~/lib/hooks/use-auth";
import { useSubscription } from "~/lib/hooks/use-subscription";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "Ceník – FirmIndex",
		description:
			"Transparentní ceny bez skrytých poplatků. Vyberte si plán, který odpovídá vašim potřebám.",
		canonical: `${SITE_URL}/cenik`,
	});
}

type PlanKey = "free" | "premium" | "pro" | "enterprise";

const tiers: {
	key: PlanKey;
	name: string;
	price: string;
	annualPrice: string;
	period: string;
	cta: string;
	highlighted: boolean;
}[] = [
	{
		key: "free",
		name: "Zdarma",
		price: "0 Kč",
		annualPrice: "0 Kč",
		period: "",
		cta: "Začít zdarma",
		highlighted: false,
	},
	{
		key: "premium",
		name: "Premium",
		price: "699 Kč",
		annualPrice: "583 Kč",
		period: "/měs",
		cta: "Od podzimu 2026",
		highlighted: true,
	},
	{
		key: "pro",
		name: "Pro",
		price: "1 499 Kč",
		annualPrice: "1 249 Kč",
		period: "/měs",
		cta: "Od podzimu 2026",
		highlighted: false,
	},
	{
		key: "enterprise",
		name: "Enterprise",
		price: "Na míru",
		annualPrice: "Na míru",
		period: "",
		cta: "Kontaktujte nás",
		highlighted: false,
	},
];

const featureGroups = [
	{
		group: "Základní data",
		features: [
			{ name: "Vyhledávání firem", values: [true, true, true, true] },
			{ name: "Registrační data", values: [true, true, true, true] },
			{ name: "Jednatelé a společníci", values: [true, true, true, true] },
			{ name: "Základní kontaktní údaje", values: [true, true, true, true] },
		],
	},
	{
		group: "Finanční data",
		features: [
			{ name: "Finanční přehled a grafy", values: [true, true, true, true] },
			{ name: "Historie finančních výsledků", values: ["3 roky", "5 let", "5 let", "5 let"] },
			{ name: "Poměrové ukazatele", values: [false, true, true, true] },
			{ name: "Detailní položky účetních výkazů", values: [false, true, true, true] },
			{
				name: "FirmIndex skóre",
				values: ["Rizikové pásmo", "Plné skóre", "Plné skóre", "Plné skóre"],
			},
			{ name: "Srovnání s odvětvím", values: [false, false, true, true] },
		],
	},
	{
		group: "Firemní události a struktura",
		features: [
			{
				name: "Firemní události z listin (fúze, kapitál, orgány)",
				values: ["10 posledních", "Kompletní", "Kompletní", "Kompletní"],
			},
			{
				name: "Správa společnosti ze stanov",
				values: ["Základní přehled", "Kompletní", "Kompletní", "Kompletní"],
			},
			{
				name: "Vztahy ve skupině (zpráva o vztazích)",
				values: ["3 poslední", "Kompletní", "Kompletní", "Kompletní"],
			},
		],
	},
	{
		group: "Monitoring",
		features: [
			{ name: "Monitoring firem", values: ["5", "100", "1 000", "Neomezeně"] },
			{ name: "E-mailová upozornění", values: [true, true, true, true] },
			{ name: "Webhook notifikace", values: [false, false, false, true] },
		],
	},
	{
		group: "Export a integrace",
		features: [
			{ name: "Export do PDF", values: [false, true, true, true] },
			{ name: "Export do Excel", values: [false, true, true, true] },
			{
				name: "API přístup",
				values: [false, true, true, true],
				footnote: "Stejné limity jako webové rozhraní",
			},
			{ name: "Hromadné exporty", values: [false, false, true, true] },
			{ name: "Vlastní integrace", values: [false, false, false, true] },
		],
	},
	{
		group: "Podpora",
		features: [
			{ name: "E-mailová podpora", values: [true, true, true, true] },
			{ name: "Prioritní podpora", values: [false, false, true, true] },
			{ name: "Dedikovaný account manažer", values: [false, false, false, true] },
			{ name: "SLA garance", values: [false, false, false, true] },
		],
	},
];

const faqs = [
	{
		q: "Co mohu využívat už teď?",
		a: "Bezplatný účet je dostupný už dnes. Získáte s ním rozšířený profil firem, tři roky finančních výsledků, monitoring pěti firem a e-mailová upozornění.",
	},
	{
		q: "Kdy budou placené plány dostupné?",
		a: "Plány Premium a Pro připravujeme na podzim 2026. Vytvořte si bezplatný účet už teď a o spuštění vás budeme informovat.",
	},
	{
		q: "Jak funguje roční fakturace?",
		a: "Po spuštění placených plánů zaplatíte při roční fakturaci za 10 měsíců místo 12. Platba proběhne jednorázově na začátku období.",
	},
	{
		q: "Co obsahuje Enterprise plán?",
		a: "Enterprise plán je přizpůsoben na míru vašim potřebám. Kontaktujte nás a připravíme vám individuální nabídku.",
	},
	{
		q: "Odkud pocházejí firemní události?",
		a: "Ze Sbírky listin obchodního rejstříku. Notářské zápisy, projekty přeměn, stanovy a zprávy o vztazích vytěžujeme pomocí AI do strukturované podoby — každý údaj nese skóre spolehlivosti (confidence) a odkaz na zdrojovou listinu. Události tak vidíte často týdny až měsíce před zápisem změny do rejstříku.",
	},
	{
		q: "Je v plánu zahrnut API přístup?",
		a: "API přístup bude po spuštění součástí plánů Premium, Pro a Enterprise. Klíče pak vytvoříte v sekci Nastavení → API klíče.",
	},
];

export default function Cenik() {
	const [annual, setAnnual] = useState(false);
	const { user } = useAuth();
	const { effectivePlan } = useSubscription();
	const navigate = useNavigate();

	const currentPlanKey = effectivePlan.toLowerCase() as PlanKey;

	function handleCta(tierKey: PlanKey) {
		if (tierKey === "free" && !user) {
			navigate("/login?mode=register");
			return;
		}

		if (tierKey === "enterprise") {
			navigate("/kontakt");
		}
	}

	function getCtaLabel(tierKey: PlanKey) {
		if (user && tierKey === currentPlanKey) return "Aktuální plán";
		if (tierKey === "free" && user) return "Účet vytvořen";
		return tiers.find((t) => t.key === tierKey)?.cta;
	}

	function isCtaDisabled(tierKey: PlanKey) {
		return (
			tierKey === "premium" ||
			tierKey === "pro" ||
			(Boolean(user) && (tierKey === "free" || tierKey === currentPlanKey))
		);
	}

	return (
		<>
			{/* Header */}
			<section className="bg-gradient-to-b from-primary-50 to-background px-6 pb-12 pt-16 lg:px-8 lg:pt-24">
				<div className="mx-auto max-w-4xl text-center">
					<h1 className="text-4xl font-extrabold tracking-tight text-text sm:text-5xl">Ceník</h1>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted">
						Transparentní ceny bez skrytých poplatků. Vyberte si plán, který odpovídá vašim
						potřebám.
					</p>
					<div className="mx-auto mt-6 flex max-w-2xl flex-col items-center gap-4 rounded-2xl bg-white px-5 py-4 text-sm shadow-card sm:flex-row sm:justify-between sm:text-left">
						<div className="flex items-start gap-3">
							<CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
							<p className="text-muted">
								<span className="font-semibold text-text">
									Placené plány spustíme na podzim 2026.
								</span>{" "}
								Vytvořte si bezplatný účet už teď a o spuštění vás budeme informovat.
							</p>
						</div>
						{!user && (
							<Link
								to="/login?mode=register"
								className="shrink-0 rounded-full bg-primary-500 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-primary-600"
							>
								Vytvořit účet zdarma
							</Link>
						)}
					</div>

					{/* Toggle */}
					<div className="mt-8 inline-flex items-center gap-1 rounded-full bg-white p-1 shadow-card">
						<button
							type="button"
							onClick={() => setAnnual(false)}
							className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
								!annual ? "bg-primary-500 text-white" : "text-muted hover:text-text"
							}`}
						>
							Měsíčně
						</button>
						<button
							type="button"
							onClick={() => setAnnual(true)}
							className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
								annual ? "bg-primary-500 text-white" : "text-muted hover:text-text"
							}`}
						>
							Ročně
							<span
								className={`ml-1.5 text-xs font-semibold ${
									annual ? "text-white/90" : "text-secondary-500"
								}`}
							>
								-2 měsíce
							</span>
						</button>
					</div>
				</div>
			</section>

			{/* Pricing cards (mobile) */}
			<section className="px-6 pb-12 lg:hidden">
				<div className="mx-auto flex max-w-md flex-col gap-6">
					{tiers.map((tier) => (
						<div
							key={tier.name}
							className={`rounded-2xl p-6 ${
								tier.highlighted
									? "bg-white shadow-lg ring-2 ring-primary-500"
									: "bg-white shadow-card"
							}`}
						>
							{tier.highlighted && (
								<span className="mb-3 inline-block rounded-full bg-primary-600 px-4 py-1 text-xs font-semibold text-white">
									Nejoblíbenější
								</span>
							)}
							<h3 className="text-lg font-semibold text-text">{tier.name}</h3>
							<div className="mt-2">
								<span className="text-3xl font-bold text-text">
									{annual ? tier.annualPrice : tier.price}
								</span>
								{tier.period && <span className="text-muted">{tier.period}</span>}
							</div>
							{(tier.key === "premium" || tier.key === "pro") && (
								<p className="mt-2 text-xs font-medium text-muted">
									Předplatné připravujeme na podzim 2026
								</p>
							)}
							<button
								type="button"
								onClick={() => handleCta(tier.key)}
								disabled={isCtaDisabled(tier.key)}
								className={`mt-6 block w-full rounded-full py-2.5 text-center text-sm font-semibold transition-colors ${
									isCtaDisabled(tier.key)
										? "cursor-not-allowed bg-gray-100 text-gray-500"
										: tier.highlighted
											? "bg-primary-600 text-white hover:bg-primary-700"
											: "bg-gray-100 text-text hover:bg-gray-200"
								}`}
							>
								{getCtaLabel(tier.key)}
							</button>
						</div>
					))}
				</div>
			</section>

			{/* Comparison table (desktop) */}
			<section className="hidden px-6 pb-20 lg:block lg:px-8">
				<div className="mx-auto max-w-7xl overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr>
								<th className="py-4 pr-4 text-left text-sm font-medium text-muted w-1/3" />
								{tiers.map((tier) => (
									<th key={tier.name} className="px-4 py-4 text-center">
										<div className="relative">
											{tier.highlighted && (
												<span className="mb-2 inline-block rounded-full bg-primary-600 px-3 py-0.5 text-xs font-semibold text-white">
													Nejoblíbenější
												</span>
											)}
											<p className="text-lg font-semibold text-text">{tier.name}</p>
											<p className="mt-1">
												<span className="text-2xl font-bold text-text">
													{annual ? tier.annualPrice : tier.price}
												</span>
												{tier.period && <span className="text-sm text-muted">{tier.period}</span>}
											</p>
											{(tier.key === "premium" || tier.key === "pro") && (
												<p className="mt-1 text-xs font-medium text-muted">
													Předplatné od podzimu 2026
												</p>
											)}
											<button
												type="button"
												onClick={() => handleCta(tier.key)}
												disabled={isCtaDisabled(tier.key)}
												className={`mt-4 inline-block rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
													isCtaDisabled(tier.key)
														? "cursor-not-allowed bg-gray-100 text-gray-500"
														: tier.highlighted
															? "bg-primary-600 text-white hover:bg-primary-700"
															: "bg-gray-100 text-text hover:bg-gray-200"
												}`}
											>
												{getCtaLabel(tier.key)}
											</button>
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{featureGroups.map((group) => (
								<Fragment key={group.group}>
									<tr>
										<td
											colSpan={5}
											className="border-t border-gray-200 pb-2 pt-6 text-sm font-semibold text-text"
										>
											{group.group}
										</td>
									</tr>
									{group.features.map((feature) => (
										<tr key={feature.name} className="border-t border-gray-100">
											<td className="py-3 pr-4 text-sm text-muted">
												<span>{feature.name}</span>
												{"footnote" in feature && feature.footnote && (
													<span className="ml-2 text-xs text-muted/70">({feature.footnote})</span>
												)}
											</td>
											{feature.values.map((val, i) => (
												<td
													key={`${feature.name}-${tiers[i].name}`}
													className="px-4 py-3 text-center"
												>
													{val === true ? (
														<Check size={18} className="mx-auto text-secondary-500" />
													) : val === false ? (
														<Minus size={18} className="mx-auto text-gray-300" />
													) : (
														<span className="text-sm font-medium text-text">{val}</span>
													)}
												</td>
											))}
										</tr>
									))}
								</Fragment>
							))}
						</tbody>
					</table>
				</div>
			</section>

			{/* FAQ */}
			<section className="bg-surface px-6 py-20 lg:px-8">
				<div className="mx-auto max-w-3xl">
					<h2 className="text-center text-3xl font-bold tracking-tight text-text">Časté dotazy</h2>
					<div className="mt-12 flex flex-col gap-6">
						{faqs.map((faq) => (
							<div key={faq.q} className="card-surface rounded-xl p-6">
								<h3 className="text-base font-semibold text-text">{faq.q}</h3>
								<p className="mt-2 text-sm text-muted leading-relaxed">{faq.a}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="bg-gradient-brand px-6 py-16 lg:px-8">
				<div className="mx-auto max-w-3xl text-center">
					<h2 className="text-2xl font-bold text-white sm:text-3xl">Začněte zdarma už dnes</h2>
					<p className="mt-4 text-white/80">
						Vytvořte si bezplatný účet. Placené plány spustíme na podzim 2026 a dáme vám vědět.
					</p>
					<Link
						to={user ? "/dashboard" : "/login?mode=register"}
						className="mt-8 inline-block rounded-full bg-white px-8 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50"
					>
						{user ? "Přejít do aplikace" : "Vytvořit účet zdarma"}
					</Link>
				</div>
			</section>
		</>
	);
}
